import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import {
  Box,
  Typography,
  TableCell,
  TableRow,
  Avatar,
  LinearProgress,
  IconButton,
  Tooltip,
  Button,
  Checkbox,
} from "@mui/material";

import { User } from "../../../../../../types/types";
import NewChatPopUp from "../../../../../../shared/NewChatPopUp";
import {
  getCandidateDetailsRoute,
  getCandidateGinoFitScoreRoute,
  getCandidateReportRoute,
} from "../../../../../../routers/routeUtils";

import { useUserDataFormatter } from "../../../../../User/hooks/useUserDataFormatter";

import { Bucket } from "../../../../enum/jobEnums";
import { JobCandidate } from "../../../../api/types/job.types";
import { useChangeCandidateStatus } from "../../../../api/mutations/useChangeCandidateStatus";
import { UnlockGinoFitScoreModal, useUnlockGinoFitScoreModal } from "../../../../components/UnlockGinoFitScoreModal";
import {
  DisregardCandidateModal,
  QualifyCandidateModal,
  useDisregardCandidateModal,
  useQualifyCandidateModal,
} from "../../../../components";
import {
  ShortListedIcon,
  QualifiedIcon,
  DisregardedIcon,
  AddReviewIcon,
  ViewReviewIcon,
  MessageIcon,
  GinoReportIcon,
  ViewReviewButtonIcon,
  AddReviewButtonIcon,
} from "../../../../assets/icons/go-hire-page-icons";

const ApplicantRow = ({
  applicantData,
  onCandidateSelection,
  selectedCandidates,
  isMaxReached,
}: {
  applicantData: JobCandidate;
  onCandidateSelection: (candidate: JobCandidate) => void;
  selectedCandidates: JobCandidate[];
  isMaxReached: boolean;
}) => {
  const { t } = useTranslation("goHire", { keyPrefix: "jobDetailsPage.candidatesTab" });
  const { t: tBucket } = useTranslation("goHire", { keyPrefix: "enum.bucket" });
  const { jobId } = useParams<{ jobId: string }>();
  const { mutate: changeStatus } = useChangeCandidateStatus();

  const { fitScore, bucket: candidateStatus, reviewId } = applicantData;
  const ginoScore = typeof fitScore === "object" ? fitScore.percent : fitScore;
  const { fullName, firstTwoChr, countryName, nationality, media, mediaFileName } = useUserDataFormatter(applicantData);

  const [searchParams] = useSearchParams();
  const candidateType = searchParams.get("candidate-type") || "all-candidates";

  const navigate = useNavigate();
  const handleNavigateToGinoReport = () => navigate(getCandidateGinoFitScoreRoute(jobId!, applicantData.id));
  const handleNavigateToReview = () => navigate(getCandidateReportRoute(jobId!, applicantData.id, reviewId!));

  const isChecked = selectedCandidates.some((c) => c.id === applicantData.id);
  const isCheckboxDisabled = isMaxReached && !isChecked;

  const isLocked =
    (candidateStatus as Bucket) === Bucket.QUALIFIED || (candidateStatus as Bucket) === Bucket.DISREGARDED;

  const canViewReview = !!reviewId,
    canViewGinoReport = false,
    canQualify = !isLocked && (candidateStatus as Bucket) !== Bucket.QUALIFIED,
    canDisregard = !isLocked && (candidateStatus as Bucket) !== Bucket.DISREGARDED,
    canShortlist = !isLocked && (candidateStatus as Bucket) !== Bucket.SHORT_LISTED;

  const [openCreateMessage, setOpenCreateMessage] = useState<boolean>(false);
  const handleOpenCreateMessage = () => setOpenCreateMessage(true);
  const handleCloseCreateMessage = () => setOpenCreateMessage(false);

  const handleShortlist = () => {
    if (!jobId) return;
    changeStatus({
      jobId,
      candidateId: applicantData.id,
      payload: { bucket: Bucket.SHORT_LISTED },
    });
  };

  return (
    <TableRow>
      {/* {candidateType !== "all-candidates" && ( */}
      <TableCell align="left">
        <Checkbox
          size="small"
          onChange={() => onCandidateSelection(applicantData)}
          checked={isChecked}
          disabled={isCheckboxDisabled}
        />
      </TableCell>
      {/* )} */}
      <TableCell align="left" sx={{ width: "100%" }}>
        <Box sx={styles.applicantInfoContainer}>
          <Avatar sx={styles.applicantAvatar} src={media} alt={mediaFileName}>
            {firstTwoChr}
          </Avatar>
          <Typography
            component={Link}
            to={getCandidateDetailsRoute(jobId!, applicantData.id)}
            sx={styles.applicantName}
          >
            {fullName}
          </Typography>
        </Box>
      </TableCell>
      {candidateType === "all-candidates" && (
        <TableCell align="left">
          <Box sx={styles.statusBadge(candidateStatus as any)}>{tBucket(candidateStatus as any)}</Box>
        </TableCell>
      )}
      <TableCell align="left">
        <Typography sx={styles.cellText}>{countryName}</Typography>
      </TableCell>
      <TableCell align="left">
        <Typography sx={styles.cellText}>{nationality}</Typography>
      </TableCell>
      <TableCell align="left">
        <CandidateFitScoreCard ginoScore={ginoScore} />
      </TableCell>
      <TableCell align="left">
        <Box sx={styles.applicantActionContainer}>
          {canShortlist && (
            <Tooltip title={t("moveToShortlist")} placement="top-end" arrow>
              <IconButton size="small" onClick={handleShortlist}>
                <ShortListedIcon />
              </IconButton>
            </Tooltip>
          )}
          {canQualify && <QualifyCandidateButton user={applicantData} />}
          {canDisregard && <DisregardCandidateButton user={applicantData} />}
          {canViewGinoReport && (
            <Tooltip title={t("ginoReport")} placement="top-end" arrow>
              <IconButton size="small" onClick={handleNavigateToGinoReport}>
                <GinoReportIcon />
              </IconButton>
            </Tooltip>
          )}
          <Tooltip title={t("message")} placement="top-end" arrow onClick={handleOpenCreateMessage}>
            <IconButton size="small">
              <MessageIcon />
            </IconButton>
          </Tooltip>
          {openCreateMessage && (
            <NewChatPopUp
              open={openCreateMessage}
              handleClose={handleCloseCreateMessage}
              // @ts-expect-error -> Need the chatpop up to be a user type
              receiverInfo={applicantData}
            />
          )}

          {canViewReview && (
            <Tooltip title={t("viewReview")} placement="top-end" arrow>
              <IconButton size="small" onClick={handleNavigateToReview}>
                <ViewReviewIcon />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      </TableCell>
    </TableRow>
  );
};

export default ApplicantRow;

const DisregardCandidateButton = ({ user }: { user: User }) => {
  const { t } = useTranslation("goHire", { keyPrefix: "jobDetailsPage.candidatesTab" });
  const { openDisregardCandidateModal, handleOpenDisregardCandidateModal, handleCloseDisregardCandidateModal } =
    useDisregardCandidateModal();

  return (
    <>
      <Tooltip title={t("moveToDisregarded")} placement="top-end" arrow>
        <IconButton size="small" onClick={handleOpenDisregardCandidateModal}>
          <DisregardedIcon />
        </IconButton>
      </Tooltip>
      <DisregardCandidateModal
        open={openDisregardCandidateModal}
        onClose={handleCloseDisregardCandidateModal}
        user={user}
      />
    </>
  );
};

const QualifyCandidateButton = ({ user }: { user: User }) => {
  const { t } = useTranslation("goHire", { keyPrefix: "jobDetailsPage.candidatesTab" });
  const { openQualifyCandidateModal, handleOpenQualifyCandidateModal, handleCloseQualifyCandidateModal } =
    useQualifyCandidateModal();

  return (
    <>
      <Tooltip title={t("moveToQualified")} placement="top-end" arrow>
        <IconButton size="small" onClick={handleOpenQualifyCandidateModal}>
          <QualifiedIcon />
        </IconButton>
      </Tooltip>
      <QualifyCandidateModal open={openQualifyCandidateModal} onClose={handleCloseQualifyCandidateModal} user={user} />
    </>
  );
};

const CandidateFitScoreCard = ({ ginoScore }: { ginoScore: number }) => {
  const isBlur = true;
  const { openUnlockGinoFitScoreModal, handleOpenUnlockGinoFitScoreModal, handleCloseUnlockGinoFitScoreModal } =
    useUnlockGinoFitScoreModal();

  if (isBlur) {
    return (
      <>
        <Box
          sx={styles.applicantFitScoreContainer(isBlur)}
          component={Button}
          onClick={handleOpenUnlockGinoFitScoreModal}
        >
          <LinearProgress variant="determinate" value={ginoScore} sx={styles.applicantFitScoreProgress} />
          <Typography sx={styles.applicantFitScorePercentage}>{ginoScore}%</Typography>
        </Box>
        <UnlockGinoFitScoreModal open={openUnlockGinoFitScoreModal} onClose={handleCloseUnlockGinoFitScoreModal} />
      </>
    );
  }

  return (
    <Box sx={styles.applicantFitScoreContainer(isBlur)}>
      <LinearProgress variant="determinate" value={ginoScore} sx={styles.applicantFitScoreProgress} />
      <Typography sx={styles.applicantFitScorePercentage}>{ginoScore}%</Typography>
    </Box>
  );
};

const styles = {
  applicantInfoContainer: {
    display: "flex",
    alignItems: "center",
    gap: "12px",
  },
  applicantAvatar: {
    height: "32px",
    width: "32px",
  },
  applicantName: {
    fontFamily: "Montserrat",
    color: "var(--Gray-600, #475467)",
    fontSize: "14px",
    fontStyle: "normal",
    fontWeight: 400,
    lineHeight: "20px",
    minWidth: "100px",
    textWrap: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    textDecoration: "none",
  },
  cellText: {
    fontFamily: "Montserrat",
    color: "var(--Gray-600, #475467)",
    fontSize: "14px",
    fontStyle: "normal",
    fontWeight: 400,
    lineHeight: "20px",
  },
  applicantFitScoreContainer: (blur: boolean) => ({
    display: "flex",
    gap: "12px",
    alignItems: "center",
    // ...(blur && {
    //   WebkitFilter: "blur(10px)",
    //   MozFilter: "blur(10px)",
    //   OFilter: "blur(10px)",
    //   msFilter: "blur(10px)",
    //   filter: "blur(10px)",
    //   WebkitBackdropFilter: "blur(10px)",
    //   MozBackdropFilter: "blur(10px)",
    //   OBackdropFilter: "blur(10px)",
    //   msBackdropFilter: "blur(10px)",
    //   backdropFilter: "blur(10px)",
    // }),
  }),
  applicantFitScoreProgress: {
    height: "8px",
    minWidth: "88px",
    borderRadius: "4px",
  },
  applicantFitScorePercentage: {
    color: "var(--Gray-700, #344054)",
    fontFamily: "Montserrat",
    fontSize: "14px",
    fontStyle: "normal",
    fontWeight: 500,
    lineHeight: "20px",
  },
  applicantActionContainer: {
    display: "flex",
    gap: "4px",
    alignItems: "center",
  },
  statusBadge: (bucket: Bucket) => {
    const styles = {
      [Bucket.APPLIED]: {
        backgroundColor: "var(--Gray-50, #F9FAFB)",
        color: "var(--Gray-700, #344054)",
      },
      [Bucket.SHORT_LISTED]: {
        backgroundColor: "var(--Warning-50, #FFFAEB)",
        color: "var(--Warning-700, #B54708)",
      },
      [Bucket.QUALIFIED]: {
        backgroundColor: "var(--Success-50, #ECFDF3)",
        color: "var(--Success-700, #027A48)",
      },
      [Bucket.DISREGARDED]: {
        backgroundColor: "var(--Error-50, #FEF3F2)",
        color: "var(--Error-700, #B42318)",
      },
    };

    return {
      display: "inline-flex",
      padding: "2px 8px",
      justifyContent: "center",
      alignItems: "center",
      borderRadius: "16px",
      fontSize: "12px",
      fontStyle: "normal",
      fontWeight: 500,
      lineHeight: "18px",
      fontFamily: "Montserrat",
      whiteSpace: "nowrap",
      ...styles[bucket],
    };
  },
};
